/**
 * Category narrative: Intelligent Resuscitation Infrastructure.
 *
 * Positioning rules baked into this content:
 * - The category phrase is used as plain language. It is NOT trademarked
 *   (no ™ / ® until PathFree approves a mark).
 * - No "first", "only", "leading", or superiority claims. Comparisons
 *   describe capability scope, not vendor quality.
 * - PathFree capability rows are written as design intent, consistent with
 *   pre-market development and validation status.
 */

export const CATEGORY_NAME = "Intelligent Resuscitation Infrastructure";

export const CATEGORY_STATEMENT =
  "Intelligent Resuscitation Infrastructure connects readiness, clinical response, documentation, data, and continuous improvement in one coordinated ecosystem.";

/** Short leadership lines used as eyebrows, pull quotes, and section framing. */
export const CATEGORY_LINES = [
  "Beyond the code cart.",
  "Beyond another clinical application.",
  "One connected intelligence layer for the resuscitation lifecycle.",
  "From equipment readiness to system-wide learning.",
  "Designed around the event — not around isolated products.",
] as const;

export const CATEGORY_LEAD =
  "PathFree Technologies is not building another storage cart, another documentation app, or another standalone model. datasyteAI™ and AiMediQ™ are designed as one infrastructure layer spanning the full arc of a resuscitation event.";

export type CategoryEra = {
  id: string;
  era: string;
  title: string;
  body: string;
  scope: string;
};

/** Four-step evolution of how hospitals have equipped resuscitation. */
export const categoryEras: CategoryEra[] = [
  {
    id: "carts",
    era: "Generation one",
    title: "Traditional emergency carts organized supplies",
    body:
      "The crash cart solved a logistics problem: put the right equipment in a known place. Everything about the event itself — timing, sequencing, decisions, and the record — stayed with the people in the room.",
    scope: "Storage",
  },
  {
    id: "apps",
    era: "Generation two",
    title: "Digital applications improved individual tasks",
    body:
      "Software addressed single tasks such as code documentation, checklists, or drill tracking. Each tool improved its own step, while the steps around it stayed disconnected.",
    scope: "One isolated function",
  },
  {
    id: "devices",
    era: "Generation three",
    title: "Connected devices generated additional event data",
    body:
      "Monitors, defibrillators, and networked equipment produced more signal per event. The data existed, but assembling it into one usable account of what happened remained manual work.",
    scope: "Multiple integrated functions",
  },
  {
    id: "infrastructure",
    era: "The category we are building",
    title: "Intelligent Resuscitation Infrastructure coordinates the complete lifecycle",
    body:
      "Readiness, response, documentation, review, and improvement are treated as one continuous system rather than five separate purchases. Clinicians stay in control of every clinical decision; the infrastructure carries the coordination.",
    scope: "Coordinated intelligence across the lifecycle",
  },
];

/** Capability scope compared across approaches. `true` = in scope by design. */
export type CategoryTier = {
  id: string;
  label: string;
  summary: string;
  emphasis?: boolean;
  /** Keyed by capability id below. */
  scope: Record<string, boolean>;
};

export const categoryCapabilities = [
  { id: "readiness", label: "Equipment and supply readiness" },
  { id: "response", label: "Real-time response support at the bedside" },
  { id: "documentation", label: "Structured capture during the event" },
  { id: "data", label: "One connected event record" },
  { id: "review", label: "Debrief and quality review inputs" },
  { id: "improvement", label: "Learning routed back to protocols and training" },
] as const;

export const categoryTiers: CategoryTier[] = [
  {
    id: "cart",
    label: "Traditional code cart",
    summary: "Storage",
    scope: {
      readiness: true,
      response: false,
      documentation: false,
      data: false,
      review: false,
      improvement: false,
    },
  },
  {
    id: "point",
    label: "Point solution",
    summary: "One isolated function",
    scope: {
      readiness: false,
      response: false,
      documentation: true,
      data: false,
      review: false,
      improvement: false,
    },
  },
  {
    id: "connected",
    label: "Connected system",
    summary: "Multiple integrated functions",
    scope: {
      readiness: true,
      response: true,
      documentation: true,
      data: true,
      review: false,
      improvement: false,
    },
  },
  {
    id: "pathfree",
    label: "PathFree vision",
    summary: "Coordinated intelligence across readiness, response, documentation, review, and improvement",
    emphasis: true,
    scope: {
      readiness: true,
      response: true,
      documentation: true,
      data: true,
      review: true,
      improvement: true,
    },
  },
];

export const CATEGORY_COMPARISON_NOTE =
  "This comparison describes the scope of capability each approach is designed to cover. It is not a performance, outcome, or superiority claim, and it does not evaluate any named product. PathFree rows describe design intent for a system in pre-market development and validation.";

export const CATEGORY_SCOPE_NOTE =
  "Intelligent Resuscitation Infrastructure is a plain-language description of the category PathFree is building toward. PathFree does not claim to be the first or only organization working in this space.";
